// src/admin/pages/CompanyDetailPage.jsx — /admin/companies/:customerId (v212). Purge-oriented, no editing (C1):
// identity, plan, its tenants (retired included, each linking to /admin/tenants?tenant=), its people, «Disattiva» /
// «Riattiva», «Elimina definitivamente…» — offered only when the company is deactivated AND every tenant is retired
// (C4, companyModel.purgeGate; greyed with the reason otherwise).
// C5 — a company whose purge STARTED answers 403 CUSTOMER_ADMIN_REQUIRED on read (superadmins too): the page shows
// «Eliminazione avviata e non terminata.» + «Completa l'eliminazione» (dialog in `resume` mode), never «Riattiva».
// After a purge: forget the console's "last tenant" keys of its tenants, never re-read the company (403/404),
// «OK» returns to the list. ⛔ Errors worded from err.code; a sequence ticket guards the reads.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import CompanyPurgeDialog from '../companies/CompanyPurgeDialog.jsx';
import { StatusChip } from './CompanyListPage.jsx';
import {
  getCompany, listCompanyTenants, listCompanyPeople, deactivateCompany, activateCompany, isPurgeStartedError,
} from '../companiesApi.js';
import { purgeGate } from '../companies/companyModel.js';
import { resultCounts } from '../purge/purgeModel.js';
import { adminErrorText, isGoneError } from '../adminErrorCopy.js';
import { clearAdminLastTenantKeys } from '../../auth/tenantPurged.js';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const cardStyle = {
  background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`,
  padding: '20px 24px', marginBottom: '20px',
};

const btnStyle = (color = COLORS.ink) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color, fontSize: '12px', cursor: 'pointer',
  fontFamily: FONT_SANS,
});

const muted = { color: COLORS.textSecondary, fontSize: '13px', margin: 0 };

export default function CompanyDetailPage() {
  const { t } = useTranslation('admin');
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [people, setPeople] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const ticket = useRef(0);
  const leaveAfterOutcome = useRef(false);

  const load = useCallback(async () => {
    ticket.current += 1;
    const mine = ticket.current;
    setLoading(true);
    const [c, tn, pp] = await Promise.allSettled([
      getCompany(customerId), listCompanyTenants(customerId), listCompanyPeople(customerId),
    ]);
    if (ticket.current !== mine) return;
    setCompany(c.status === 'fulfilled' ? c.value : null);
    setLoadError(c.status === 'rejected' ? c.reason : null);
    setTenants(tn.status === 'fulfilled' && Array.isArray(tn.value?.tenants) ? tn.value.tenants : null);
    setPeople(pp.status === 'fulfilled' && Array.isArray(pp.value?.users) ? pp.value.users : null);
    setLoading(false);
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const name = company?.legal_name || location.state?.name || customerId;
  const isInactive = company?.is_active === false;
  const gate = purgeGate(company, tenants);
  const tenantNames = (tenants || []).map((tn) => tn.display_name || tn.id);

  async function handleLifecycle() {
    let row;
    try {
      row = isInactive ? await activateCompany(customerId) : await deactivateCompany(customerId);
    } catch (err) {
      throw new Error(adminErrorText(t, err)); // ConfirmDialog shows it inline
    }
    await load();
    setOutcome({
      tone: 'success',
      text: row?.is_active === false
        ? t('companyDetail.outcomeDeactivated', { name })
        : t('companyDetail.outcomeReactivated', { name }),
    });
  }

  function handlePurged(result) {
    setPurgeOpen(false);
    (tenants || []).forEach((tn) => clearAdminLastTenantKeys(tn.id));
    const { deleted, anonymised } = resultCounts(result);
    const accounts = deleted + anonymised > 0 ? ` ${t('purge.tenant.outcomeAccounts', { deleted, anonymised })}` : '';
    leaveAfterOutcome.current = true; // the company is gone: «OK» returns to the list
    setOutcome({ tone: 'success', text: `${t('purge.company.outcome', { name })}${accounts}` });
  }

  function closePurge({ gone } = {}) {
    setPurgeOpen(false);
    if (gone) navigate('/admin/companies', { replace: true });
  }

  function closeOutcome() {
    setOutcome(null);
    if (leaveAfterOutcome.current) {
      leaveAfterOutcome.current = false;
      navigate('/admin/companies', { replace: true });
    }
  }

  const back = (
    <button type="button" onClick={() => navigate('/admin/companies')} style={{ ...btnStyle(), marginBottom: '20px', background: 'transparent', border: 'none' }}>
      <ArrowLeft size={15} /> {t('companyDetail.back')}
    </button>
  );
  const purgeDialog = (
    <CompanyPurgeDialog
      open={purgeOpen}
      customerId={customerId}
      name={name}
      tenantNames={tenantNames}
      resume={isPurgeStartedError(loadError)}
      onPurged={handlePurged}
      onClose={closePurge}
    />
  );
  const page = (children) => (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '900px' }}>
      {back}
      {children}
      {purgeDialog}
      <OutcomeDialog outcome={outcome} onClose={closeOutcome} />
    </div>
  );

  if (loading && !company && !loadError) return page(<p style={muted}>{t('shared.loading')}</p>);

  if (isPurgeStartedError(loadError)) {
    return page(
      <div style={cardStyle}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px' }}>{name}</h2>
        <p style={{ ...muted, color: COLORS.ink, marginBottom: '12px' }}>{t('companyDetail.purgeStarted')}</p>
        <button type="button" onClick={() => setPurgeOpen(true)} style={btnStyle(SEMANTIC.errore.fg)}>
          {t('companyDetail.completePurge')}
        </button>
      </div>
    );
  }
  if (loadError) {
    const text = isGoneError(loadError) ? t('companyDetail.notFound') : adminErrorText(t, loadError);
    return page(<p role="alert" style={{ ...muted, color: SEMANTIC.errore.fg }}>{text}</p>);
  }
  if (!company) return page(null);

  return page(
    <>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>{company.legal_name}</h2>
          <StatusChip isActive={company.is_active} t={t} />
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: COLORS.textSecondary, marginBottom: '16px' }}>
          <span>{t('companyDetail.id')} <code style={{ fontFamily: FONT_MONO }}>{company.id}</code></span>
          <span>{t('companyDetail.vat')} <code style={{ fontFamily: FONT_MONO }}>{company.vat_number || '—'}</code></span>
          <span>{t('companyDetail.plan')} {company.plan_id || t('companyDetail.noPlan')}</span>
          {company.tech_email && <span>{t('companyDetail.contact')} {company.tech_email}</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setLifecycleOpen(true)}
            style={btnStyle(isInactive ? SEMANTIC.verde.fg : SEMANTIC.errore.fg)}
          >
            {isInactive ? t('companyDetail.reactivate') : t('companyDetail.deactivate')}
          </button>
          <button
            type="button"
            onClick={() => setPurgeOpen(true)}
            disabled={!gate.allowed}
            title={gate.allowed ? undefined : t(gate.reasonKey, gate.params)}
            style={{ ...btnStyle(SEMANTIC.errore.fg), opacity: gate.allowed ? 1 : 0.5, cursor: gate.allowed ? 'pointer' : 'not-allowed' }}
          >
            {t('companyDetail.purge')}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 12px' }}>{t('companyDetail.tenantsTitle')}</h3>
        {tenants === null && <p style={muted}>{t('companyDetail.tenantsUnknown')}</p>}
        {tenants?.length === 0 && <p style={muted}>{t('companyDetail.tenantsEmpty')}</p>}
        {tenants?.length > 0 && (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {tenants.map((tn) => (
              <li key={tn.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', fontSize: '13px' }}>
                <Link to={`/admin/tenants?tenant=${encodeURIComponent(tn.id)}`} style={{ color: COLORS.accent }}>
                  {tn.display_name || tn.id}
                </Link>
                {tn.is_active === false && <span style={{ fontSize: '11px', color: SEMANTIC.attenzione.fg }}>{t('purge.tenant.retired')}</span>}
                {tn.is_active === true && <span style={{ fontSize: '11px', color: SEMANTIC.verde.fg }}>{t('purge.tenant.active')}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 12px' }}>{t('companyDetail.peopleTitle')}</h3>
        {people === null && <p style={muted}>{t('companyDetail.peopleUnknown')}</p>}
        {people?.length === 0 && <p style={muted}>{t('companyDetail.peopleEmpty')}</p>}
        {people?.length > 0 && (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {people.map((p) => (
              <li key={p.user_id} style={{ display: 'flex', gap: '10px', padding: '6px 0', fontSize: '13px' }}>
                <Link to={`/admin/users/${encodeURIComponent(p.user_id)}`} style={{ color: COLORS.accent }}>{p.email}</Link>
                <span style={{ color: COLORS.textSecondary }}>
                  {t(`companyDetail.roles.${p.role}`, { defaultValue: p.role || '' })}
                </span>
                {p.is_active === false && <span style={{ color: COLORS.textSecondary }}>· {t('shared.inactive')}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={lifecycleOpen}
        onClose={() => setLifecycleOpen(false)}
        onConfirm={handleLifecycle}
        title={isInactive ? t('companyDetail.reactivateTitle') : t('companyDetail.deactivateTitle')}
        message={isInactive
          ? t('companyDetail.reactivateMessage', { name })
          : t('companyDetail.deactivateMessage', { name })}
        confirmLabel={isInactive ? t('companyDetail.reactivate') : t('companyDetail.deactivate')}
        variant={isInactive ? 'default' : 'danger'}
      />
    </>
  );
}
